import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff, KeyRound, UserCircle } from 'lucide-react';
import { usersApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input, Label, FieldError, FieldHint } from '../components/ui/input';
import { friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import { useAuth } from '../lib/auth-context';

const MIN_PASSWORD_LENGTH = 10;

/** Same floor the API enforces (users.ts's STRONG_PASSWORD) — kept in sync so the meter never promises a password the server will then reject. */
function passwordRequirementsMet(password: string): boolean {
  return (
    password.length >= MIN_PASSWORD_LENGTH && /[a-z]/.test(password) && /[A-Z]/.test(password) && /[0-9]/.test(password)
  );
}

type StrengthLabel = 'Very weak' | 'Weak' | 'Fair' | 'Strong';

function passwordStrength(password: string): { score: number; label: StrengthLabel } {
  if (!password) return { score: 0, label: 'Very weak' };
  let score = 0;
  if (password.length >= MIN_PASSWORD_LENGTH) score++;
  if (password.length >= 16) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  const label: StrengthLabel = score <= 1 ? 'Very weak' : score === 2 ? 'Weak' : score <= 3 ? 'Fair' : 'Strong';
  return { score, label };
}

const STRENGTH_COLOR: Record<StrengthLabel, string> = {
  'Very weak': 'bg-danger',
  Weak: 'bg-warning',
  Fair: 'bg-success/60',
  Strong: 'bg-success',
};

function PasswordStrengthMeter({ password }: { password: string }) {
  const { score, label } = passwordStrength(password);
  const segments = 5;
  if (!password) return null;
  return (
    <div className="space-y-1" aria-live="polite">
      <div className="flex gap-1">
        {Array.from({ length: segments }, (_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full ${i < score ? STRENGTH_COLOR[label] : 'bg-surface-sunken'}`}
          />
        ))}
      </div>
      <p className="text-xs text-ink-muted">
        Strength: <span className="font-medium text-ink">{label}</span>
      </p>
    </div>
  );
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  error,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: string;
  error?: string | null;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          required
          className="pr-10"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-ink-faint hover:text-ink-muted"
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      <FieldError>{error}</FieldError>
    </div>
  );
}

function ChangePasswordForm() {
  const { toast } = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => usersApi.changePassword({ currentPassword, newPassword }),
    onSuccess: () => {
      toast({ title: 'Password changed', description: 'Use your new password next time you sign in.', variant: 'success' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setFormError(null);
    },
    onError: (err) => setFormError(friendlyErrorMessage(err)),
  });

  const confirmMismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;
  const newPasswordTooWeak = newPassword.length > 0 && !passwordRequirementsMet(newPassword);

  // Weak-password and mismatch feedback is already shown inline (newPasswordTooWeak /
  // confirmMismatch below), reactively as the user types — submit just re-checks the same
  // conditions to block the request without also duplicating those messages in formError,
  // which is reserved for errors the server reports back (e.g. wrong current password).
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    if (!passwordRequirementsMet(newPassword) || newPassword !== confirmPassword) return;
    mutation.mutate();
  };

  return (
    <form className="max-w-md space-y-5" onSubmit={handleSubmit} noValidate>
      <PasswordField
        id="current-password"
        label="Current password"
        value={currentPassword}
        onChange={setCurrentPassword}
        autoComplete="current-password"
      />

      <div className="space-y-2">
        <PasswordField
          id="new-password"
          label="New password"
          value={newPassword}
          onChange={setNewPassword}
          autoComplete="new-password"
          error={newPasswordTooWeak ? `At least ${MIN_PASSWORD_LENGTH} characters, with uppercase, lowercase, and a number.` : null}
        />
        <PasswordStrengthMeter password={newPassword} />
        <FieldHint>{`At least ${MIN_PASSWORD_LENGTH} characters, including an uppercase letter, a lowercase letter, and a number.`}</FieldHint>
      </div>

      <PasswordField
        id="confirm-password"
        label="Confirm new password"
        value={confirmPassword}
        onChange={setConfirmPassword}
        autoComplete="new-password"
        error={confirmMismatch ? "New password and confirmation don't match." : null}
      />

      {formError ? (
        <p role="alert" className="text-xs text-danger">
          {formError}
        </p>
      ) : null}

      <Button type="submit" disabled={mutation.isPending}>
        <KeyRound size={16} /> Change password
      </Button>
    </form>
  );
}

export function Account() {
  const { principal } = useAuth();

  return (
    <div>
      <PageHeader title="Account" description="Manage your own sign-in details." />

      <Card>
        <CardContent className="space-y-1 pt-6">
          <div className="flex items-center gap-2 text-ink-muted">
            <UserCircle size={18} />
            <p className="text-sm">
              Signed in as <span className="font-medium text-ink">{principal?.userId}</span>
              {principal?.role ? ` (${principal.role})` : ''}
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="mt-6">
        <h2 className="mb-3 text-sm font-semibold text-ink">Change password</h2>
        <Card>
          <CardContent className="pt-6">
            <ChangePasswordForm />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
